import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import { isPortrait, height } from '../../utils/size';
import { LoadingSpinner } from '../LoadingSpinner';
import ThemeContext from '../../utils/theming';

function buttonHeight(numRows, isVisualLink) {
    const def = isPortrait() ? 60 : numRows > 1 ? 65 : 88;
    const wrapper = document.querySelector('#ButtonsWrapper');
    if (!wrapper) {
        return def;
    }
    const available =
        height() - wrapper.offsetTop - (isVisualLink && isPortrait() ? 180 : 40);
    if (available < 40 || available > height() * 0.9) {
        return def;
    }
    const preferred = (available * 0.95) / numRows - (isPortrait() ? 8 : 0);
    return Math.max(Math.min(preferred, 100), 30);
}

const numRows = answersLength =>
    isPortrait() ? answersLength : answersLength > 3 ? 2 : 1;

const buttonFontSize = (numRows, longText, veryLongText, isVisualLink) => {
    const validSizes = [16, 18, 20, 24, 26, 28];

    const space = buttonHeight(numRows, isVisualLink);
    const preferred = 3 * Math.sqrt(space);
    let size = validSizes.find(s => s >= preferred) || validSizes[validSizes.length - 1];
    if (longText) size *= 18 / 28;
    if (veryLongText) size *= 16 / 20;
    return size;
};

const ButtonParent_OrientationBasedRules = props => {
    if (isPortrait()) {
        return `
            width: 100%;
            margin: 0 0px 16px 0px;
            padding: ${props.answersLength > 1 ? `0 8px` : `0`};
        `;
    }
    return `
        margin: ${props.answersLength > 1 ? `16px 8px 0px 8px;` : `0`};
        width: ${
            props.answersLength === 3
                ? 'calc(33.33% - 16px)'
                : props.answersLength === 1
                ? '100%'
                : 'calc(50% - 16px)'
        };`;
};

export const ButtonParent = styled.div`
    float: left;
    position: relative;

    ${props => ButtonParent_OrientationBasedRules(props)};

    > div {
        display: none;
        position: absolute;
        left: 50%;
        margin-left: -11px;
        top: 50%;
        margin-top: -11px;
        width: 22px !important;
        height: 22px !important;
    }

    button {
        text-transform: 'none';
        font-size: ${props =>
            buttonFontSize(
                numRows(props.answersLength),
                props.longText,
                props.veryLongText,
                props.isVisualLink
            ) + 'px'};
        line-height: ${props =>
            buttonFontSize(
                numRows(props.answersLength),
                props.longText,
                props.veryLongText,
                props.isVisualLink
            ) +
            4 +
            'px'};
    }
`;

const ButtonsWrapper_OrientationBasedRules = props => {
    if (isPortrait()) {
        return `
          left: ${props.mobileLeftPosition ? props.mobileLeftPosition + 'px;' : '16px;'};
          width: ${
              props.answersLength === 1 && !props.mobileStyled
                  ? 'calc(100% - 32px);'
                  : 'calc(100% - 16px);'
          };
      `;
    }
    return `
      ${
          props.answersLength === 1
              ? 'width: calc(100% - 32px);'
              : 'width: calc(100% - 16px); left: 1em;'
      };
    `;
};

export const ButtonsWrapper = styled.div`
    flex: 1;
    justify-content: center;
    display: flex;
    ${({ isVisualLink }) => isVisualLink && `position: relative;`};
    > div {
        margin: ${props =>
            props.answersLength === 4
                ? '22px -8px 0 -8px'
                : props.answersLength === 1
                ? '54px 0 0 0'
                : '54px -8px 0 -8px'};
        ${props => props.isVisualLink && isPortrait() && `margin-top: 0;`};
        div:last-child {
            ${() => isPortrait() && `margin-bottom: 0px !important`};
        }

        position: absolute;
        opacity: 1;
        bottom: ${({ mobileStyled }) => (isPortrait() && mobileStyled ? '55px' : '42px')};
        ${({ isVisualLink }) => isVisualLink && 'bottom: 10px'};
        top: ${({ isVisualLink }) => (isVisualLink && isPortrait() ? 0 : `auto`)};

        ${props => ButtonsWrapper_OrientationBasedRules(props)};

        button {
            width: 100%;
            height: ${({ answersLength, isVisualLink }) =>
                buttonHeight(numRows(answersLength), isVisualLink) + 'px'};
            cursor: pointer;
            background: #fff;
            border-radius: 4px;
            border: solid 1px #f1f1ee;
            font-weight: 600;
            margin: 0;
            vertical-align: top;
            padding-left: 10px;
            padding-right: 10px;
            outline: none;
            -webkit-tap-highlight-color: rgba(255, 255, 255, 1);

            &.selected {
                text-indent: -9999px;
                background: ${({ theme }) => theme.colors.primary};
                border: ${({ theme }) => `solid 1px ${theme.colors.primary}`};
                outline: none;

                + div {
                    display: block;
                }
            }

            ${({ answersLength, isVisualLink }) =>
                isPortrait() &&
                'height: ' + buttonHeight(answersLength, isVisualLink) + 'px;'};

            ${({ isMobile, theme }) =>
                isMobile
                    ? `
                        &:focus {
                            background: ${theme.colors.primary};
                            border: solid 1px ${theme.colors.primary};
                            color: #FFF;
                            outline: none;
                        }
                    `
                    : `
                        &:hover {
                            background: ${theme.colors.primary};
                            border: solid 1px ${theme.colors.primary};
                            color: #FFF;
                            outline: none;
                        }`};
        }
    }

    #recordVideoStartEmailButton{
        left: 1rem;
    }
`;

const Container = styled.div``;
export const ButtonsContainer = props => <Container id={props.id}>{props.children}</Container>;
ButtonsContainer.propTypes = {
    children: PropTypes.array.isRequired
};

const useForceUpdate = () => {
  const [, setIt] = useState(false);
  return () => setIt(it => !it);
};

let lastUpdated = 0

export const AutoScalingButtonSet = props => {
    const forceUpdate = useForceUpdate();
    const theme = useContext(ThemeContext);
    const children = Array.isArray(props.children) ? props.children : [props.children];
    const maxTextLength = children
        .map(child => child.props.text || '')
        .reduce((max, text) => Math.max(max, text.length), 0);

    //This forces the button to reupdate after the rest of the dom finishes updating, otherwise it often resizes incorrectly based on stale dom data in the buttonHeight function.
    if ((Date.now() - lastUpdated) > 100) {
        setTimeout(() => {
            forceUpdate();
            lastUpdated = Date.now();
        }, 100)
    }

    return (
        <ButtonsWrapper
            theme={theme}
            className="ie-auto-scaling-button-set"
            id="ButtonsWrapper"
            data-button-wrapper="true"
            answersLength={children.length}
            isMobile={props.isMobile}
            mobileStyled={props.mobileStyled}
            mobileLeftPosition={props.mobileLeftPosition}
            isVisualLink={props.isVisualLink}
        >
            <ButtonsContainer id={props.id}>
                {children.map((child, i) => {
                    return (
                        <ButtonParent
                            key={child.key + i}
                            answersLength={children.length}
                            longText={maxTextLength > 20}
                            veryLongText={maxTextLength > 50}
                            isVisualLink={props.isVisualLink}
                        >
                            {child}
                            {child.props.useLoadingSpinner && <LoadingSpinner />}
                        </ButtonParent>
                    );
                })}
            </ButtonsContainer>
        </ButtonsWrapper>
    );
};
AutoScalingButtonSet.propTypes = {
    children: PropTypes.any.isRequired, // array of buttons
    isMobile: PropTypes.bool,
    isVisualLink: PropTypes.bool,
    mobileStyled: PropTypes.bool,
    mobileLeftPosition: PropTypes.number
};
